/**
 * The `/limits` tab vocabulary, and where the three surfaces it replaced land.
 *
 * Pure and React-free, the shape `engagement-tabs.ts` established: the redirect
 * table, the page, and the tests all have to agree on these strings, and a
 * literal repeated in three places is a literal that eventually disagrees with
 * itself.
 *
 * **Why this module exists at all.** `/limits` was the one consolidated hub
 * whose tabs were NOT addressable — an uncontrolled `<Tabs defaultValue>` that
 * read nothing from the URL — while `/spend-caps`, `/usage-alarms` and
 * `/rate-limits` all forwarded to it bare. So every bookmark to any of the
 * three landed on Budget: a person who had saved the alarms page arrived at
 * spending ceilings and had to know that alarms were now a tab, which is the
 * exact cost the consolidation was supposed to remove. It also meant no tab of
 * this page could be linked from anywhere — an email, a support reply, a
 * cross-link from `/analytics` — where every other hub can be.
 */

export const LIMITS_PATH = '/limits';

/**
 * Ordered left to right, and the order is the page's argument: what it costs,
 * who gets told, and what it is allowed to do.
 */
export const LIMITS_TABS = ['budget', 'alerts', 'traffic'] as const;
export type LimitsTab = (typeof LIMITS_TABS)[number];

/** The query param carrying the tab, matching every other hub's `?tab=`. */
export const LIMITS_TAB_PARAM = 'tab';

export function isLimitsTab(value: string): value is LimitsTab {
  return (LIMITS_TABS as readonly string[]).includes(value);
}

/**
 * The tab this render shows, given the URL and whether the role can see the
 * money half of the page.
 *
 * Two fallbacks, and they are different questions rather than one:
 *
 *  - An unknown value (a rename, a typo, a truncated share) falls back rather
 *    than rendering a blank surface. A stale bookmark should still show the
 *    page.
 *  - `budget` from a role without `billing:view` falls back too, because that
 *    tab does not exist for them — the trigger is not rendered, so an
 *    uncorrected value would leave Radix with a value no trigger claims and
 *    show NO tab body at all. An editor following an old `/spend-caps` link is
 *    the live case: they cannot see spending, and the honest landing is the
 *    page's own default for their role rather than an empty panel.
 */
export function resolveLimitsTab(search: URLSearchParams, canViewBudget: boolean): LimitsTab {
  const fallback: LimitsTab = canViewBudget ? 'budget' : 'traffic';
  const raw = search.get(LIMITS_TAB_PARAM) ?? '';
  if (!isLimitsTab(raw)) return fallback;
  return raw === 'budget' && !canViewBudget ? fallback : raw;
}

/** A linkable path for one tab — used by the redirects and by cross-links. */
export function limitsTabPath(tab: LimitsTab): string {
  return `${LIMITS_PATH}?${LIMITS_TAB_PARAM}=${tab}`;
}

/**
 * Where each retired surface's bookmarks land.
 *
 * One entry per page the consolidation absorbed, and each names the tab that
 * absorbed it rather than the page root — the whole point of making the tabs
 * addressable. The redirects preserve the incoming search, which is safe
 * because `mergeRedirectSearch` is destination-wins: the tab named here always
 * survives, and anything else the bookmark carried rides along.
 */
export const RETIRED_LIMIT_SURFACE_TABS: Readonly<Record<string, LimitsTab>> = {
  '/spend-caps': 'budget',
  '/usage-alarms': 'alerts',
  '/rate-limits': 'traffic',
};
